import React, { useEffect, useState, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { getAuth } from "firebase/auth";
import { getPlayerPortfolio } from "../utils/portfolioUtils";
import PlayerProfileTab from "./PlayerProfileTab";
import PlayerPortfolioTab from "./PlayerPortfolioTab";
import { motion, AnimatePresence } from "framer-motion";
import { getNextZ } from "../utils/zIndexManager";
import PlayerInventoryTab from "./PlayerInventoryTab";
import "./PlayerProfileModal.css";

const inFlightRequests = {};
const profileCache = {};
const CACHE_TTL = 60 * 1000;

export default function PlayerProfileModal({ playerSelected, onClose }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [showModal, setShowModal] = useState(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);

  const regionMap = {
    false: "Global",
    AF: "Africa",
    AS: "Asia",
    AU: "Australia",
    EU: "Europe",
    NA: "North America",
    SA: "South America",
  };

  const playerName =
    typeof playerSelected === "string" ? playerSelected : playerSelected?.name;

 useEffect(() => {
  if (!playerName) {
    setShowModal(false);
    return;
  }

  setShowModal(true);
  setLoading(true);
  setProfile(null);
  setPortfolio(null);
  setError(null);

  const fetchData = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) throw new Error("Not logged in");

      const token = await user.getIdToken();

      const now = Date.now();

      const cached = profileCache[playerName];
      if (cached && now - cached.timestamp < CACHE_TTL) {
        setProfile(cached.profile);
        setPortfolio(cached.portfolio);
        setLoading(false);
        return;
      }

      if (inFlightRequests[playerName]) {
        const result = await inFlightRequests[playerName];
        setProfile(result.profile);
        setPortfolio(result.portfolio);
        setLoading(false);
        return;
      }

      inFlightRequests[playerName] = (async () => {
        const res = await fetch(
          "https://getplayerprofile-5dhfwyjvzq-uc.a.run.app",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ name: playerName }),
          }
        );

        if (res.status === 429) {
          throw new Error("Rate limited (429)");
        }

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (!data.getUserProfile) {
          throw new Error("No player found");
        }

        const profileData = data.getUserProfile;

        const portfolioData = await getPlayerPortfolio(playerName, token);
        const finalPortfolio = portfolioData?.companyHoldings || [];

        const result = {
          profile: profileData,
          portfolio: finalPortfolio,
        };

        profileCache[playerName] = {
          ...result,
          timestamp: Date.now(),
        };

        return result;
      })();

      const result = await inFlightRequests[playerName];

      setProfile(result.profile);
      setPortfolio(result.portfolio);
    } catch (err) {
      console.error("Error loading profile/portfolio:", err);
      setError("Failed to load data.");
    } finally {
      setLoading(false);
      delete inFlightRequests[playerName];
    }
  };

  fetchData();
}, [playerName]);

  const startDrag = (clientX, clientY) => {
    setDragging(true);
    setZIndex(getNextZ() + 10000);
    dragStartRef.current = {
      x: clientX - position.x,
      y: clientY - position.y,
    };
  };

  const doDrag = useCallback(
    (clientX, clientY) => {
      if (!dragging) return;
      setPosition({
        x: clientX - dragStartRef.current.x,
        y: clientY - dragStartRef.current.y,
      });
    },
    [dragging]
  );

  useEffect(() => {
    const mouseMove = (e) => doDrag(e.clientX, e.clientY);
    const touchMove = (e) =>
      doDrag(e.touches[0].clientX, e.touches[0].clientY);
    const stop = () => setDragging(false);

    window.addEventListener("mousemove", mouseMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchmove", touchMove);
    window.addEventListener("touchend", stop);

    return () => {
      window.removeEventListener("mousemove", mouseMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchmove", touchMove);
      window.removeEventListener("touchend", stop);
    };
  }, [doDrag]);

  const formatCurrency = (val) =>
    Number(val || 0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });

  const formatNumber = (val) =>
    Number(val || 0).toLocaleString("en-US");

 const modalContent = (
    <AnimatePresence>
      {showModal && (
        <div className="floating-modal-wrapper" style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex }}>
          <motion.div
            key="modal"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="tabbed-panel"
            style={{
              position: "absolute",
              top: `calc(20% + ${position.y}px)`,
              left: `calc(50% + ${position.x}px)`,
              transform: "translateX(-50%)",
              width: "420px",
              minHeight: "450px",
              background: "rgba(10, 10, 10, 0.95)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              pointerEvents: "auto",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 20px 50px rgba(0,0,0,0.5)"
            }}
          >

            <div
              className="draggable-header"
              onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
              style={{
                padding: "12px",
                cursor: "grab",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "rgba(255, 255, 255, 0.03)",
                borderBottom: "1px solid rgba(255, 255, 255, 0.05)"
              }}
            >
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: '0.75rem', color: '#8b949e', letterSpacing: '1px' }}>
                TRADER VIEW | {playerName?.toUpperCase()}
              </span>
              <button className="close-btn" onClick={onClose}>&times;</button>
            </div>

            <div className="tab-navigation-container">
              <div className="nav-modules-row">
                {["profile", "portfolio", "inventory"].map((tab) => (
                  <div
                    key={tab}
                    className={`nav-module ${activeTab === tab ? "active" : ""}`}
                    onClick={() => setActiveTab(tab)}
                  >
                    <span className="module-label">{tab}</span>
                  </div>
                ))}
              </div>
            </div>

<div className="scrollable-content" style={{ flex: 1, overflowY: "auto", padding: "15px" }}>
              {loading ? (
                <p className="terminal-status">LOADING...</p>
              ) : error ? (
                <p className="terminal-status error">ERROR: {error}</p>
              ) : activeTab === "profile" ? (
                <PlayerProfileTab 
                  profile={profile} 
                  formatNumber={formatNumber} 
                  formatCurrency={formatCurrency}
                  regionMap={regionMap}
                />
              ) : activeTab === "portfolio" ? (
                <PlayerPortfolioTab
                  playerName={profile?.userDisplayName}
                  profile={profile} 
                  portfolio={portfolio}
                  formatCurrency={formatCurrency}
                  formatNumber={formatNumber}
                />
              ) : (
                <PlayerInventoryTab 
                  playerName={profile?.userDisplayName} 
                  profile={profile} 
                />
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}